import { Component, CUSTOM_ELEMENTS_SCHEMA, Input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { LoaderComponent } from '../../../shared-components/loader/loader.component';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrService } from '../../../services/toastr-service';
import { CellTemplateDetails, TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import { debounceTime, distinctUntilChanged, map, ReplaySubject, takeUntil } from 'rxjs';
import { DmrlService } from '../dmrl.service';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { DMRLFilter, DMRLFilterGrid } from '../../../models/dmrl-filter.model';
import { DMRLFilterForm } from '../../../form-model/dmrl-filter-form.model';
import { WorkOrderSearchTextForm } from '../../../form-model/work-order-search-text.form.model';
import { DMRLHeader } from '../../../models/dmrl-header.model';
import { utils } from '../../../helper/utils';

@Component({
  selector: 'app-dmrl-grid',
  imports: [NgxSkeletonLoaderModule, RouterModule, LoaderComponent, FormsModule, ReactiveFormsModule],
  templateUrl: './dmrl-grid.component.html',
  styleUrl: './dmrl-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DmrlGridComponent {
  data: any;
  mockDataForLoadingAnimation: { id: number }[] = [...Array(1).keys()].map((i) => ({ id: i }));
  firstPage: number | undefined;
  previousPage: number | undefined;
  currentPage: number = 1;
  nextPage: number | undefined;
  lastPage: number | undefined;
  totalItemCount: number | undefined;
  apiVersion: string | undefined;
  pageLimit: number = 20;
  sort: string | undefined;
  emptyMessage: string = $localize`Enter Equipment Id(s) to get their DMRL value.`;
  emptySubMessage: string = '';
  getDmrlListApiCallStatus: APICallStatus = 'NOT_STARTED';
  filterParam: DMRLFilterGrid = {};
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    dmrlFilterForm: FormGroup<DMRLFilterForm>;
    workOrderSearchTextForm: FormGroup<WorkOrderSearchTextForm>;
  }>;
  columns: Array<TableColumn> = [
    {
      id: 'equipmentNumber',
      label: 'Equipment Number',
      sortDisabled: true,
    },
    {
      id: 'containerType',
      label: 'Container Type',
      sortDisabled: true,
    },
    {
      id: 'productionYear',
      label: 'Production Year',
      sortDisabled: true,
    },
    {
      id: 'age',
      label: 'Age',
      sortDisabled: true,
    },
    {
      id: 'currentLocationCode',
      label: 'Current Location',
      sortDisabled: true,
    },
    {
      id: 'mrlBox',
      label: 'MRL Box',
      sortDisabled: true,
      cellTemplate: (row: CellTemplateDetails) => {
        const value = row.rowData['mrlBox'] != null ? (row.rowData['mrlBox'] as string) : '-----';
        return row.html`<span style="background-color: var(--mds_brand_appearance_warning_weak_background-color); color: var(--mds_brand_appearance_warning_weak_on-background-color); padding: 2px 8px; border-radius: 4px; font-weight: 600;">${value}</span>`;
      },
    },
    {
      id: 'mrlUnit',
      label: 'MRL Unit',
      sortDisabled: true,
      cellTemplate: (row: CellTemplateDetails) => {
        const value = row.rowData['mrlUnit'] != null ? (row.rowData['mrlUnit'] as string) : '-----';
        return row.html`<span style="background-color: var(--mds_brand_appearance_warning_weak_background-color); color: var(--mds_brand_appearance_warning_weak_on-background-color); padding: 2px 8px; border-radius: 4px; font-weight: 600;">${value}</span>`;
      },
    },

    {
      id: 'equipmentProfile',
      label: 'Profile',
      sortDisabled: true,
    },
    {
      id: 'reeferUnitManufacturerName',
      label: 'Reefer Make',
      sortDisabled: true,
      cellTemplate: (row: any) => {
        if (row.rowData.reeferUnitManufacturerName) {
          return row.rowData.reeferUnitManufacturerName;
        } else {
          return '-----';
        }
      },
    },
    {
      id: 'reeferUnitModelName',
      label: 'Reefer Model',
      sortDisabled: true,
      cellTemplate: (row: any) => {
        if (row.rowData.reeferUnitModelName) {
          return row.rowData.reeferUnitModelName;
        } else {
          return '-----';
        }
      },
    },
    {
      id: 'moveTimestamp',
      label: 'Move Timestamp',
      sortDisabled: true,
    },
    {
      id: 'move',
      label: 'Move',
      sortDisabled: true,
    },
    {
      id: 'equipmentSubType',
      label: 'Equipment Sub Type',
      sortDisabled: true,
    },
    {
      id: 'sizeCode',
      label: 'Size Code',
      sortDisabled: true,
    },
    {
      id: 'isSales',
      label: 'Is Sales',
      sortDisabled: true,
    },
    {
      id: 'isSurplus',
      label: 'Is Surplus',
      sortDisabled: true,
    },
    {
      id: 'updatedAt',
      label: 'Updated At',
      sortDisabled: true,
    },
  ];

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private dmrlService: DmrlService,
  ) {}
  ngOnInit() {
    this.sort = `containerId:asc`;
    //this.getContainerMRL();
    this.subscribeToFormChanges();
  }
  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
  private subscribeToFormChanges() {
    this.filterFormGroup.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.applyDmrlFilter.value ||
        this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.value
      ) {
        this.onApplyFilter();
        this.filterFormGroup.controls.dmrlFilterForm.controls.applyDmrlFilter.setValue(false);
        this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter.setValue(false);
      }
    });
  }
  onSortDirectionChange(event: Event) {
    let customEvent = event as CustomEvent;
    let sortColumn: string = this.sortColumnName(customEvent.detail.column);
    let sortDirection: string = customEvent.detail.direction == 'ascending' ? 'asc' : 'desc';
    if (sortColumn != 'id') {
      this.sort = `${sortColumn}:${sortDirection}`;
      this.currentPage = 1;
      this.getContainerMRL();
    }
  }
  private sortColumnName(gridColumnName: string): string {
    return gridColumnName;
  }

  onPageLimitChange(event: Event) {
    let customEvent = event as CustomEvent;
    this.currentPage = 1;
    this.pageLimit = customEvent.detail.value;
    this.getContainerMRL();
  }

  onPageChange(event: Event) {
    let customEvent = event as CustomEvent;
    let changedPage = customEvent.detail as number;
    if (changedPage != this.currentPage) {
      this.currentPage = changedPage;
      this.getContainerMRL();
    }
  }
  onApplyFilter() {
    if (this.filterFormGroup.controls.dmrlFilterForm.valid) {
      const filters: DMRLFilterGrid = {};
      if (this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentID.value) {
        filters.containerNumbers = this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentID.value;
      }
      if (this.filterFormGroup.controls.dmrlFilterForm.controls.locationCode.value) {
        filters.locationCodes = this.filterFormGroup.controls.dmrlFilterForm.controls.locationCode.value;
      }
      if (this.filterFormGroup.controls.dmrlFilterForm.controls.profile.value) {
        filters.profiles = this.filterFormGroup.controls.dmrlFilterForm.controls.profile.value.toString();
      }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.productionYear.value &&
        this.filterFormGroup.controls.dmrlFilterForm.controls.productionYear.value.length > 0
      ) {
        filters.productionYears = this.filterFormGroup.controls.dmrlFilterForm.controls.productionYear.value
          .map((x: any) => x.toString().replace(/\-/g, ','))
          .join(',');
      }
      // if (this.filterFormGroup.controls.dmrlFilterForm.controls.profile.value && this.filterFormGroup.controls.dmrlFilterForm.controls.profile.value.length>0) {
      //   filters.profiles = this.filterFormGroup.controls.dmrlFilterForm.controls.profile.value.map((x:any) => x.profile.replace(/\-/g, ',')).join(',');
      // }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentGroup.value &&
        this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentGroup.value.length > 0
      ) {
        filters.equipmentGroups = this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentGroup.value
          .map((x: any) => x.equipmentGroup.replace(/\-/g, ','))
          .join(',');
      }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentOwner.value &&
        this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentOwner.value.length > 0
      ) {
        filters.equipmentOwners = this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentOwner.value
          .map((x: any) => x.equipmentOwner.replace(/\-/g, ','))
          .join(',');
      }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentManufacturer.value &&
        this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentManufacturer.value.length > 0
      ) {
        filters.equipmentManufacturers = this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentManufacturer.value
          .map((x: any) => x.equipmentManufacturer.replace(/\-/g, ','))
          .join(',');
      }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentReeferType.value &&
        this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentReeferType.value.length > 0
      ) {
        filters.equipmentReeferTypes = this.filterFormGroup.controls.dmrlFilterForm.controls.equipmentReeferType.value
          .map((x: any) => x.equipmentRefeerType.replace(/\-/g, ','))
          .join(',');
      }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.containerType.value &&
        this.filterFormGroup.controls.dmrlFilterForm.controls.containerType.value.length > 0
      ) {
        filters.containerTypes = this.filterFormGroup.controls.dmrlFilterForm.controls.containerType.value
          .map((x: any) => x.containerType.replace(/\-/g, ','))
          .join(',');
      }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.isSales.value === true ||
        this.filterFormGroup.controls.dmrlFilterForm.controls.isSales.value === false
      ) {
        filters.isSales = this.filterFormGroup.controls.dmrlFilterForm.controls.isSales.value;
      }
      if (this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value) {
        filters.searchText = this.filterFormGroup.controls.workOrderSearchTextForm.controls.searchText.value;
      }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.isSurplus.value === true ||
        this.filterFormGroup.controls.dmrlFilterForm.controls.isSurplus.value === false
      ) {
        filters.isSurplus = this.filterFormGroup.controls.dmrlFilterForm.controls.isSurplus.value;
      }
      if (
        this.filterFormGroup.controls.dmrlFilterForm.controls.isEmpty.value === true ||
        this.filterFormGroup.controls.dmrlFilterForm.controls.isEmpty.value === false
      ) {
        filters.isEmpty = this.filterFormGroup.controls.dmrlFilterForm.controls.isEmpty.value;
      }
      this.filterParam = filters;
      this.currentPage = 1;
      this.getContainerMRL();
    }
  }
  getContainerMRL(): void {
    this.data = [];
    this.getDmrlListApiCallStatus = 'IN_PROGRESS';
    this.filterParam.containerNumbers = this.filterParam.containerNumbers ? this.filterParam.containerNumbers : [];
    this.filterParam.locationCodes = this.filterParam.locationCodes ? this.filterParam.locationCodes : [];
    this.filterParam.profiles = this.filterParam.profiles ? this.filterParam.profiles : '';
    this.filterParam.productionYears = this.filterParam.productionYears ? this.filterParam.productionYears : '';
    this.filterParam.equipmentGroups = this.filterParam.equipmentGroups ? this.filterParam.equipmentGroups : '';
    this.filterParam.equipmentOwners = this.filterParam.equipmentOwners ? this.filterParam.equipmentOwners : '';
    this.filterParam.equipmentManufacturers = this.filterParam.equipmentManufacturers ? this.filterParam.equipmentManufacturers : '';
    this.filterParam.equipmentReeferTypes = this.filterParam.equipmentReeferTypes ? this.filterParam.equipmentReeferTypes : '';
    this.filterParam.containerTypes = this.filterParam.containerTypes ? this.filterParam.containerTypes : '';
    this.filterParam.isSales = this.filterParam.isSales;
    this.filterParam.isSurplus = this.filterParam.isSurplus;
    this.filterParam.isEmpty = this.filterParam.isEmpty;
    this.filterParam.searchText = this.filterParam.searchText ? this.filterParam.searchText : '';
    this.dmrlService
      .getContainerMRL(this.filterParam)
      .pipe(
        takeUntil(this.destroyed$),
        map((response: DMRLHeader[]) => {
          return response.map((item, index) => ({
            ...item,
            moveTimestamp: new Date(item.moveTimestamp).toLocaleDateString(utils.locale, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
            updatedAt: new Date(item.updatedAt).toLocaleDateString(utils.locale, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            }),
          }));
        }),
      )
      .subscribe({
        next: (response) => {
          this.data = response;
          this.getDmrlListApiCallStatus = 'COMPLETED_SUCCESFULLY';
          if (this.data?.length > 0) {
            this.dmrlService.filterData = this.filterParam;
          } else if (
            this.data?.length == 0 &&
            ((this.filterParam.containerNumbers?.length ?? 0) > 0 || this.filterParam.searchText != '')
          ) {
            this.emptyMessage = $localize`No equipment found for the applied filters.`;
            this.emptySubMessage = $localize`Try changing or removing some filters to find the equipment you're looking for.`;
          } else {
            this.emptyMessage = $localize`Enter Equipment Id(s) to get their DMRL value.`;
            this.emptySubMessage = '';
          }
        },
        error: (err) => {
          this.getDmrlListApiCallStatus = 'FAILED';
          this.data = [];
        },
      });
  }
}
